import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import APIConstants from 'constants/api';
import { API } from 'action-types/api';
import store from '../store';
import { generateErrors } from './util';
import { IApiErrorAction, IApiResetErrorAction } from 'reducers/api';
import { CONFIG } from 'configs/index';

export const apiRequest = async (
	url: string,
	config: AxiosRequestConfig,
	skipLoading?: boolean,
	isBackground?: boolean,
) => {
	if (skipLoading !== true && isBackground !== true) {
		store.dispatch({ type: API.REQUEST_START });
	}
	if (isBackground !== true) {
		store.dispatch<IApiResetErrorAction>({
			type: API.RESET_ERROR,
			endpoint: url,
		});
	}
	try {
		if (url.indexOf(CONFIG.API.FrontDoor) > -1) {
			const newParams = config.params
				? { ...config.params }
				: {};
			newParams.code = CONFIG.API.ApiCode;
			config = {
				...config,
				params: {
					...newParams,
				},
			};
		}
		const res = await axios.request({
			...config,
			url,
		});
		if (skipLoading !== true && isBackground !== true) {
			store.dispatch({ type: API.REQUEST_END });
		}
		return res;
	} catch (e) {
		if (skipLoading !== true && isBackground !== true) {
			store.dispatch({ type: API.REQUEST_END });
		}
		if (isBackground !== true) {
			store.dispatch<IApiErrorAction>({
				type: API.ADD_ERROR,
				endpoint: url,
				errors: generateErrors(e),
			});
		}
		throw e;
	}
};
export const getHeaders = (url: string, config: AxiosRequestConfig): any => {
	const headers = {
		...config.headers,
		...APIConstants.Headers,
	};
	if (url.indexOf(CONFIG.API.FrontDoor) > -1) {
		headers['Application-Key'] = CONFIG.API.ApiKey;
	}
	return headers;
};

export const getHeadersApiV2 = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersApiV2,
	};
	if (url.indexOf(CONFIG.API.FrontDoor) > -1) {
		headers['Application-Key'] = CONFIG.API.ApiKey;
	}
	return headers;
};

export const getHeadersApiV3 = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersApiV3,
	};
	if (url.indexOf(CONFIG.API.FrontDoor) > -1) {
		headers['Application-Key'] = CONFIG.API.ApiKey;
	}
	return headers;
};

export const getHeadersApiV1 = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersApiV1,
	};
	if (url.indexOf(CONFIG.API.FrontDoor) > -1) {
		headers['Application-Key'] = CONFIG.API.ApiKey;
	}
	return headers;
};

export const getHeadersUploadExport = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersUploadExport,
	};
	headers['Application-Key'] = CONFIG.API.ApiKey;
	return headers;
};
export const getExtraDocument = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersExtraDocument,
	};
	headers['Application-Key'] = CONFIG.API.ApiKey;
	return headers;
};

export const getHeadersForToggle = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersFeatureToggle,
	};
	return headers;
};

export const getHeadersForUam = (
	url: string,
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersUam,
	};
	return headers;
};

export const callApiGet = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeaders(url, config);
	if (headers.ApplicationKey === undefined) {
		headers['Application-Key'] = CONFIG.API.ApiKey;
	}
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};

export const callApiV3Get = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeadersApiV3(url, config);
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};

export const callApiGetForFeatureToggle = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeadersForToggle(url, config);
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};

export const callApiGetForUam = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeadersForUam(url, config);
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};

export const callApiPostUploadExport = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
): AxiosPromise => {
	const headers = getHeadersUploadExport(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	});
};
export const callApiExtraDocument = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
): AxiosPromise => {
	const headers = getExtraDocument(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	});
};

export const callApiPost = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
): AxiosPromise => {
	const headers = getHeaders(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	});
};

export const callApiV2Post = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
	isBackground?: boolean,
): AxiosPromise => {
	const headers = getHeadersApiV2(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	},
		undefined,
		isBackground,
	);
};

export const callApiV2Get = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeadersApiV2(url, config);
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};

export const callApiV3Post = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
): AxiosPromise => {
	const headers = getHeadersApiV3(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	});
};

export const callApiV1Post = (
	url: string,
	data: any[] | object = {},
	config: AxiosRequestConfig = {},
): AxiosPromise => {
	const headers = getHeadersApiV1(url, config);
	return apiRequest(url, {
		...config,
		method: 'POST',
		headers,
		data,
	});
};

export const callApiPut = (
	url: string,
	data: object,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeaders(url, config);
	return apiRequest(url, {
		...config,
		method: 'PUT',
		headers,
		data,
	});
};

export const callApiV2Put = (
	url: string,
	data: object,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeadersApiV2(url, config);
	return apiRequest(url, {
		...config,
		method: 'PUT',
		headers,
		data,
	});
}

export const callApiV3Put = (
	url: string,
	data: object,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeadersApiV3(url, config);
	return apiRequest(url, {
		...config,
		method: 'PUT',
		headers,
		data,
	});
};

export const callApiPatch = (
	url: string,
	data: object,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeaders(url, config);
	return apiRequest(url, {
		...config,
		method: 'PATCH',
		headers,
		data,
	});
};

export const callApiDelete = (
	url: string,
	data: any,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeaders(url, config);
	return apiRequest(url, {
		...config,
		method: 'DELETE',
		headers,
		data,
	});
};

export const callApiV3Delete = (
	url: string,
	data: any,
	config: AxiosRequestConfig = {},
) => {
	const headers = getHeadersApiV3(url, config);
	return apiRequest(url, {
		...config,
		method: 'DELETE',
		headers,
		data,
	});
};

export const getHeadersSettingsApi = (
	config: AxiosRequestConfig,
): any => {
	const headers = {
		...config.headers,
		...APIConstants.HeadersSettings,
	};
	return headers;
};

export const callSettingsApiGet = (
	url: string,
	query?: object,
	config: AxiosRequestConfig = {},
	skipLoading?: boolean,
) => {
	const headers = getHeadersSettingsApi(config);
	return apiRequest(
		url,
		{
			...config,
			method: 'GET',
			params: query ? { ...query } : undefined,
			headers,
		},
		skipLoading,
	);
};